/**
 * Iter52.83 · Full Report de MyAdmin (Device Management).
 * Iter52.84 · Grupo G: activaciones estimadas al instalar que la primera
 * conexión real de Geotab puede sustituir.
 * Foto de lo que Geotab sabe de cada equipo, cruzada con la app. Solo
 * lectura salvo los grupos A y G, que se aprueban leyendo antes el efecto
 * que tienen sobre el cobro.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, Download, Search, UploadCloud } from 'lucide-react';
import { saasApi, errText } from './api';

const COLOR = {
  A: 'bg-emerald-50 border-emerald-300 text-emerald-900',
  G: 'bg-teal-50 border-teal-300 text-teal-900',
  B: 'bg-slate-50 border-slate-200 text-slate-700',
  C: 'bg-amber-50 border-amber-300 text-amber-900',
  D: 'bg-rose-50 border-rose-300 text-rose-900',
  E: 'bg-rose-50 border-rose-300 text-rose-900',
  F: 'bg-slate-50 border-slate-200 text-slate-700',
};
const GRUPOS = ['A', 'G', 'B', 'C', 'D', 'E', 'F'];
const APROBABLES = ['A', 'G'];
const fd = (v) => (v ? String(v).slice(0, 10).split('-').reverse().join('/') : '—');

export default function FullReport(){
  const [file,setFile]=useState(null);
  const [busy,setBusy]=useState(false);
  const [analisis,setAnalisis]=useState(null);
  const [historico,setHistorico]=useState([]);
  const [abierto,setAbierto]=useState('A');
  const [detalle,setDetalle]=useState({});
  const [q,setQ]=useState('');
  const [sel,setSel]=useState([]);
  const [grupoSel,setGrupoSel]=useState('A');
  const [confirmar,setConfirmar]=useState(false);
  const [leido,setLeido]=useState(false);

  const cargar=async(id)=>{
    try{
      const r=await saasApi.get('/saas/admin/control-geotab/full-report/analisis');
      const items=r.data?.items||[];
      setHistorico(items);
      const elegido=id||items[0]?.id;
      if(elegido){
        const d=await saasApi.get(`/saas/admin/control-geotab/full-report/analisis/${elegido}`);
        setAnalisis(d.data); setDetalle({}); setSel([]);
      }
    }catch(e){ toast.error(errText(e)); }
  };
  useEffect(()=>{ cargar(); /* eslint-disable-next-line */ },[]);

  // Prudente: solo vienen marcadas las que no cambian el cobro.
  const seguras=(items)=>items.filter(x=>!x.cambia_cobro).map(x=>x.suscripcion_id);
  const aprobablesDe=(g)=>(detalle[g]?.items||[]).filter(x=>g==='A'||x.accionable);

  const verGrupo=async(g)=>{
    setAbierto(abierto===g?null:g);
    if(!analisis || abierto===g) return;
    if(APROBABLES.includes(g)){
      setGrupoSel(g);
      if(detalle[g]) setSel(seguras(aprobablesDe(g)));
    }
    if(detalle[g]) return;
    try{
      const r=await saasApi.get(`/saas/admin/control-geotab/full-report/analisis/${analisis.id}/grupo/${g}`);
      setDetalle(d=>({...d,[g]:r.data}));
      if(APROBABLES.includes(g)){
        setSel(seguras((r.data.items||[]).filter(x=>g==='A'||x.accionable)));
      }
    }catch(e){ toast.error(errText(e)); }
  };

  const subir=async()=>{
    if(!file) return;
    setBusy(true);
    const fd2=new FormData(); fd2.append('csv_file',file);
    try{
      const r=await saasApi.post('/saas/admin/control-geotab/full-report/analizar',fd2,
        {headers:{'Content-Type':'multipart/form-data'}});
      toast.success(`Fichero leído: ${r.data.filas} equipos`);
      setFile(null); await cargar(r.data.id);
    }catch(e){ toast.error(errText(e)); }
    setBusy(false);
  };

  const filasAprobables = aprobablesDe(grupoSel);
  const seleccionadas = useMemo(
    ()=>filasAprobables.filter(x=>sel.includes(x.suscripcion_id)),
    [filasAprobables,sel]);
  const cambianCobro = seleccionadas.filter(x=>x.cambia_cobro);
  const importeAfectado = cambianCobro.reduce((a,x)=>a+Number(x.importe_mensual||0),0);
  const mesesCerrados = [...new Set(cambianCobro.map(x=>x.mes_inicio_cobro).filter(Boolean))];

  const aprobar=async()=>{
    setBusy(true);
    try{
      const r=await saasApi.post(
        `/saas/admin/control-geotab/full-report/analisis/${analisis.id}/aprobar`,
        {suscripcion_ids:sel, confirmado_efecto:leido, grupo:grupoSel});
      toast.success(`${r.data.aplicadas} activación(es) registradas${r.data.ya_estaban?` · ${r.data.ya_estaban} ya cambiadas`:''}`);
      if(r.data.errores?.length) toast.warning(`${r.data.errores.length} fila(s) rechazadas: ${r.data.errores[0].motivo}`);
      setConfirmar(false); setLeido(false);
      await cargar(analisis.id);
    }catch(e){ toast.error(errText(e)); }
    setBusy(false);
  };

  const filtrar=(items)=>{
    const t=q.trim().toLowerCase();
    if(!t) return items;
    return items.filter(x=>[x.serie,x.matricula,x.cliente,x.cliente_geotab]
      .some(v=>String(v||'').toLowerCase().includes(t)));
  };

  const Tabla=({g,items,conCheck})=> items.length===0
    ? <p className="text-sm text-slate-500 py-3">Sin filas.</p>
    : <table className="w-full text-xs">
      <thead><tr className="text-left text-slate-500 border-b">
        {conCheck && <th className="py-1 pr-2"></th>}
        <th className="py-1 pr-2">Serie</th><th className="py-1 pr-2">Matrícula</th>
        <th className="py-1 pr-2">Cliente</th><th className="py-1 pr-2">Estado app</th>
        <th className="py-1 pr-2">Estado Geotab</th>
        {g==='G' && <th className="py-1 pr-2">Activación estimada</th>}
        <th className="py-1 pr-2">1ª conexión</th>
        {g!=='G' && <th className="py-1 pr-2">Activación app</th>}
        <th className="py-1 pr-2">{g==='G'?(conCheck?'Diferencia (días)':'Días desde la estimada'):'Días'}</th>
        {APROBABLES.includes(g) && <th className="py-1 pr-2">Efecto si apruebas</th>}
        {g==='B' && <th className="py-1 pr-2">Afecta al inicio de cobro</th>}
      </tr></thead>
      <tbody>{items.map((x,i)=>
        <tr key={x.suscripcion_id||x.serie||i} className="border-b border-slate-100" data-testid={`full-report-fila-${g}-${x.serie||i}`}>
          {conCheck && <td className="py-1 pr-2"><input type="checkbox" data-testid={`full-report-chk-${x.suscripcion_id}`}
            checked={sel.includes(x.suscripcion_id)}
            onChange={()=>setSel(s=>s.includes(x.suscripcion_id)?s.filter(y=>y!==x.suscripcion_id):[...s,x.suscripcion_id])}/></td>}
          <td className="py-1 pr-2 font-mono">{x.serie||'—'}</td>
          <td className="py-1 pr-2">{x.matricula||'—'}</td>
          <td className="py-1 pr-2">{x.cliente||x.cliente_geotab||'—'}</td>
          <td className="py-1 pr-2">{x.estado_app||'—'}</td>
          <td className="py-1 pr-2">{x.estado_geotab||'—'}</td>
          {g==='G' && <td className="py-1 pr-2">{fd(x.activacion_estimada_fecha)}</td>}
          <td className="py-1 pr-2">{fd(x.primera_conexion)}</td>
          {g!=='G' && <td className="py-1 pr-2">{fd(x.activacion_app)}{x.activacion_estimada?' (estimada)':''}</td>}
          <td className="py-1 pr-2 tabular-nums">{(g==='G'&&!conCheck?x.dias_desde_estimada:x.dias_diferencia)??'—'}</td>
          {APROBABLES.includes(g) && <td className="py-1 pr-2">{x.efecto}{x.importe_mensual&&x.cambia_cobro?` · ${Number(x.importe_mensual).toFixed(2)} €/mes`:''}</td>}
          {g==='B' && <td className="py-1 pr-2">{x.afecta_inicio_cobro?<b className="text-rose-700">sí</b>:'no'}</td>}
        </tr>)}</tbody>
    </table>;

  return <div className="space-y-4" data-testid="full-report">
    <div className="bg-white border rounded-2xl p-5">
      <h2 className="text-xl font-black">Full Report de MyAdmin</h2>
      <p className="text-sm text-slate-500 mt-1">
        CSV de <b>Device Management → Full Report</b>. Es una foto de lo que Geotab sabe de cada equipo:
        no manda sobre la app. Solo se pueden aprobar dos cosas: las activaciones del grupo A y las
        estimadas de instalación del grupo G.
      </p>
      <div className="flex items-center gap-2 mt-3 flex-wrap">
        <input type="file" accept=".csv" data-testid="full-report-file"
          onChange={e=>setFile(e.target.files?.[0]||null)} className="text-sm"/>
        <button className="btn-primary" disabled={!file||busy} onClick={subir} data-testid="full-report-subir">
          <UploadCloud className="w-4 h-4 inline mr-1"/>{busy?'Leyendo…':'Analizar fichero'}
        </button>
        {analisis && <a className="btn-secondary" data-testid="full-report-excel"
          href={`${process.env.REACT_APP_BACKEND_URL}/api/saas/admin/control-geotab/full-report/analisis/${analisis.id}/excel`}>
          <Download className="w-4 h-4 inline mr-1"/>Excel
        </a>}
        {historico.length>1 && <select className="input w-auto text-sm" data-testid="full-report-historico"
          value={analisis?.id||''} onChange={e=>cargar(e.target.value)}>
          {historico.map(h=><option key={h.id} value={h.id}>{String(h.created_at).slice(0,16).replace('T',' ')} · {h.filename}</option>)}
        </select>}
      </div>
    </div>

    {analisis && <>
      {analisis.pendientes_aprobar>0 && <div className="bg-emerald-50 border border-emerald-300 rounded-xl p-3 text-sm text-emerald-900" data-testid="full-report-aviso-pendientes">
        <AlertTriangle className="w-4 h-4 inline mr-1"/>
        Hay <b>{analisis.pendientes_aprobar}</b> activación(es) propuestas sin revisar (grupos A y G).
      </div>}
      <div className="text-xs text-slate-500" data-testid="full-report-cabecera">
        {analisis.filas} equipos en el fichero · encabezados en la línea {analisis.fila_cabecera} ·
        {' '}{analisis.suscripciones_evaluadas} suscripciones vivas evaluadas ·
        {' '}subido por {analisis.created_by} el {String(analisis.created_at).slice(0,16).replace('T',' ')}
      </div>
      {(analisis.avisos||[]).map((a,i)=><div key={i} className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">{a}</div>)}

      <div className="flex items-center gap-2">
        <Search className="w-4 h-4 text-slate-400"/>
        <input className="input text-sm" placeholder="Filtrar por cliente, serie o matrícula"
          value={q} onChange={e=>setQ(e.target.value)} data-testid="full-report-filtro"/>
      </div>

      {GRUPOS.map(g=>{
        const r=analisis.resumen?.[g]||{};
        const items=detalle[g]?.items||[];
        const aprob=APROBABLES.includes(g);
        return <div key={g} className={`border rounded-xl ${COLOR[g]}`} data-testid={`full-report-grupo-${g}`}>
          <button className="w-full text-left p-3" onClick={()=>verGrupo(g)} data-testid={`full-report-toggle-${g}`}>
            <div className="flex justify-between items-center gap-3">
              <span className="font-bold">{g} · {r.etiqueta}</span>
              <span className="text-sm font-black tabular-nums" data-testid={`full-report-n-${g}`}>{r.n}</span>
            </div>
            <p className="text-xs mt-1 opacity-80">{r.ayuda}</p>
            {g==='B' && r.comparables!=null && <p className="text-xs mt-1 opacity-70">{r.coinciden} coinciden de {r.comparables} comparables</p>}
            {g==='G' && r.accionables!=null && <p className="text-xs mt-1 opacity-70">{r.accionables} sustituibles · {r.sin_conexion} sin conexión todavía</p>}
            {g==='F' && r.sin_id_geotab!=null && <p className="text-xs mt-1 opacity-70">{r.sin_id_geotab} suscripciones sin ID Geotab · {r.serie_desconocida} series que Geotab no conoce</p>}
          </button>
          {abierto===g && detalle[g] && <div className="bg-white border-t p-3 overflow-x-auto">
            {g==='G' ? <>
              <Tabla g="G" items={filtrar(items.filter(x=>x.accionable))} conCheck/>
              {items.some(x=>x.sin_conexion) && <div className="mt-4" data-testid="full-report-g-sin-conexion">
                <p className="text-xs font-bold text-amber-800 mb-1">
                  Estimadas sin conexión todavía · {items.filter(x=>x.sin_conexion).length}
                </p>
                <p className="text-xs text-slate-500 mb-2">
                  Instalaciones registradas que Geotab todavía no ve conectadas. Sin botón: solo aviso.
                </p>
                <Tabla g="G" items={filtrar(items.filter(x=>x.sin_conexion))}/>
              </div>}
            </> : g==='F' ? <>
              <p className="text-xs font-bold mb-1">Suscripciones sin ID Geotab · {items.filter(x=>x.sin_id_geotab).length}</p>
              <p className="text-xs text-slate-500 mb-2">Se resuelven subiendo los IDs (alta o Excel de actualización).</p>
              <Tabla g="F" items={filtrar(items.filter(x=>x.sin_id_geotab))}/>
              <p className="text-xs font-bold mt-4 mb-1" data-testid="full-report-f-desconocidas">Serie que Geotab no conoce · {items.filter(x=>!x.sin_id_geotab).length}</p>
              <Tabla g="F" items={filtrar(items.filter(x=>!x.sin_id_geotab))}/>
            </> : <Tabla g={g} items={filtrar(items)} conCheck={g==='A'}/>}
            {aprob && aprobablesDe(g).length>0 && <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
              <button className="text-xs underline" data-testid="full-report-marcar-seguras"
                onClick={()=>{setGrupoSel(g);setSel(seguras(aprobablesDe(g)));}}>
                Aprobar todas las que no cambian el cobro
              </button>
              <button className="btn-primary" disabled={!sel.length||grupoSel!==g} data-testid="full-report-abrir-confirmar"
                onClick={()=>{setLeido(false);setConfirmar(true);}}>
                Revisar y aprobar ({grupoSel===g?sel.length:0})
              </button>
            </div>}
            {!aprob && <p className="text-xs text-slate-500 mt-2">Este grupo no se aplica desde aquí: se corrige por el Excel de actualización, Dispositivos → Editar o la baja.</p>}
          </div>}
        </div>;
      })}
    </>}

    {confirmar && <div className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full p-5 space-y-3" data-testid="full-report-confirmacion">
        <h3 className="text-lg font-black">Antes de aprobar · grupo {grupoSel}</h3>
        <ul className="text-sm space-y-1">
          <li>· Filas elegidas: <b>{sel.length}</b></li>
          <li>· Cambian el cobro: <b className={cambianCobro.length?'text-rose-700':''}>{cambianCobro.length}</b></li>
          <li>· Importe mensual afectado: <b>{importeAfectado.toFixed(2)} €</b></li>
          {mesesCerrados.length>0 && <li>· Meses del nuevo inicio de cobro: {mesesCerrados.join(', ')} · si alguno está cerrado, ese tramo iría por ajuste (R47)</li>}
          {grupoSel==='G' && <li>· La activación pasa a ser la primera conexión real y se quita la marca de estimada.</li>}
        </ul>
        <p className="text-xs text-slate-500">La activación se puede corregir después a mano en la ficha del dispositivo.</p>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={leido} onChange={e=>setLeido(e.target.checked)} data-testid="full-report-he-leido"/>
          He leído el efecto
        </label>
        <div className="flex justify-end gap-2">
          <button className="btn-secondary" onClick={()=>setConfirmar(false)} data-testid="full-report-cancelar">Cancelar</button>
          <button className="btn-primary" disabled={!leido||busy} onClick={aprobar} data-testid="full-report-aprobar">
            <CheckCircle2 className="w-4 h-4 inline mr-1"/>Aprobar {sel.length} activaciones
          </button>
        </div>
      </div>
    </div>}
  </div>;
}
